import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { PLATFORM_META, STATUS_META, imageSrc } from "../lib/api";
import { FileStack, Clock, Send, TimerReset, TrendingUp } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, tint, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, delay }}
    className="glass rounded-2xl p-5 hover:-translate-y-0.5 transition-transform duration-200"
    data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
  >
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${tint}`}>
      <Icon className="w-5 h-5" />
    </div>
    <p className="text-3xl font-heading font-extrabold text-slate-50 tracking-tight">{value}</p>
    <p className="text-xs text-slate-400 font-medium mt-1">{label}</p>
  </motion.div>
);

export const DashboardOverview = ({ goTo }) => {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    // Without this catch a failed request left `stats` null forever, which
    // renders as an endless loading state with nothing explaining why.
    setError("");
    try {
      const { data } = await api.get("/stats");
      setStats(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load your stats. The server is not responding.");
    }
  };
  useEffect(() => { load(); }, []);

  const maxCh = stats ? Math.max(1, ...Object.values(stats.by_platform)) : 1;

  return (
    <div className="space-y-8" data-testid="dashboard-view">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold tracking-wider uppercase text-indigo-400 mb-2">Command Center</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-50">
            Your calendar on autopilot
          </h1>
          <p className="text-slate-400 text-sm mt-2 max-w-xl">
            Generate AI visuals & captions, route them through Telegram / WhatsApp approval, then publish across channels.
          </p>
        </div>
        <button
          onClick={() => goTo("composer")}
          data-testid="dashboard-create-btn"
          className="px-5 py-2.5 rounded-full bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold active:scale-[0.98] transition-all shadow-[0_0_24px_rgba(99,102,241,0.35)]"
        >
          + New AI Post
        </button>
      </div>

      {error && (
        <div
          data-testid="dashboard-error"
          className="flex flex-wrap items-center gap-3 rounded-xl border border-rose-900/40 bg-rose-950/30 px-4 py-3"
        >
          <p className="text-sm text-rose-300 flex-1 min-w-[200px]">{error}</p>
          <button
            onClick={load}
            className="px-3 py-1.5 rounded-lg bg-rose-500/15 hover:bg-rose-500/25 text-rose-200 text-xs font-semibold transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FileStack} label="Total Posts" value={stats?.total ?? "—"} tint="bg-indigo-500/15 text-indigo-300" delay={0.02} />
        <StatCard icon={Clock} label="Pending Approval" value={stats?.pending ?? "—"} tint="bg-amber-500/15 text-amber-300" delay={0.06} />
        <StatCard icon={TimerReset} label="Scheduled" value={stats?.scheduled ?? "—"} tint="bg-purple-500/15 text-purple-300" delay={0.1} />
        <StatCard icon={Send} label="Published" value={stats?.posted ?? "—"} tint="bg-sky-500/15 text-sky-300" delay={0.14} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="glass rounded-2xl p-6 lg:col-span-1">
          <div className="flex items-center gap-2 mb-5">
            <TrendingUp className="w-4 h-4 text-indigo-300" />
            <h3 className="font-heading font-bold text-slate-100">Channel Mix</h3>
          </div>
          <div className="space-y-4">
            {Object.entries(PLATFORM_META).map(([key, meta]) => {
              const val = stats?.by_platform?.[key] ?? 0;
              return (
                <div key={key}>
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-slate-300 font-medium">{meta.label}</span>
                    <span className="text-slate-500 font-mono">{val}</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(val / maxCh) * 100}%` }}
                      transition={{ duration: 0.6 }}
                      className="h-full rounded-full"
                      style={{ backgroundColor: meta.color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="glass rounded-2xl p-6 lg:col-span-2">
          <h3 className="font-heading font-bold text-slate-100 mb-5">Upcoming scheduled</h3>
          {stats?.upcoming?.length ? (
            <div className="space-y-3">
              {stats.upcoming.map((p) => (
                <div key={p.id} className="flex items-center gap-4 p-3 rounded-xl bg-slate-900/60 border border-slate-800" data-testid="upcoming-item">
                  {imageSrc(p) ? (
                    <img src={imageSrc(p)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center text-slate-600 text-lg">✦</div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-100 truncate">{p.topic}</p>
                    <p className="text-xs text-slate-500">{new Date(p.scheduled_at).toLocaleString()}</p>
                  </div>
                  <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${STATUS_META[p.status]?.cls}`}>
                    {STATUS_META[p.status]?.label}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10">
              <p className="text-slate-500 text-sm mb-4">No scheduled posts yet.</p>
              <button onClick={() => goTo("autopilot")} data-testid="dashboard-plan-btn" className="text-indigo-400 text-sm font-semibold hover:text-indigo-300">
                Generate a weekly plan →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
